/** Presentation helpers for a tool call: what to call it and what it acts on. */

/** Tools that move the host UI. */
export const isNavigationTool = (name: string): boolean => /^navigate/i.test(name);

/** `DeleteObjects`, `put_bucket_policy`, `navigateToRoute` → "Delete objects", … */
export const humanizeToolName = (name: string): string => {
  const words = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Schema keyword a tool provider sets on the parameters that name the resource a call acts on. */
export const RESOURCE_KEYWORD = 'x-resource';

type Schema = Record<string, unknown>;

const isSchema = (value: unknown): value is Schema =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const resolvePointer = (root: Schema, ref: string): unknown => {
  if (!ref.startsWith('#')) return undefined;
  return ref
    .slice(1)
    .split('/')
    .filter(Boolean)
    .map((part) => decodeURIComponent(part).replace(/~1/g, '/').replace(/~0/g, '~'))
    .reduce<unknown>((node, part) => (isSchema(node) ? node[part] : undefined), root);
};

/**
 * The resources a call acts on: every non-empty string argument whose schema carries
 * `x-resource`, at any depth — so a batch tool such as `DeleteObjects` lists each
 * `Objects[].Key`. Follows `$ref`, `items`, `prefixItems` and combinators. A tool that
 * flags nothing has no resources. Deduplicated, in argument order.
 */
export const toolCallResources = (args: unknown, inputSchema: unknown): string[] => {
  const found = new Set<string>();
  if (!isSchema(inputSchema)) return [];

  const branches = (schema: unknown, seen: Set<Schema>): Schema[] => {
    if (!isSchema(schema) || seen.has(schema)) return [];
    seen.add(schema);
    const nested = [
      typeof schema.$ref === 'string' ? resolvePointer(inputSchema, schema.$ref) : undefined,
      ...['allOf', 'anyOf', 'oneOf'].flatMap((key) => (Array.isArray(schema[key]) ? schema[key] : [])),
    ];
    return [schema, ...nested.flatMap((branch) => branches(branch, seen))];
  };

  const walk = (value: unknown, schema: unknown) => {
    for (const branch of branches(schema, new Set())) {
      if (typeof value === 'string') {
        if (branch[RESOURCE_KEYWORD] === true && value.length > 0) found.add(value);
      } else if (Array.isArray(value)) {
        const prefix = Array.isArray(branch.prefixItems) ? branch.prefixItems : [];
        value.forEach((item, i) => {
          walk(item, i < prefix.length ? prefix[i] : branch.items);
        });
      } else if (isSchema(value) && isSchema(branch.properties)) {
        const properties = branch.properties;
        for (const [key, child] of Object.entries(value)) walk(child, properties[key]);
      }
    }
  };

  walk(args, inputSchema);
  return [...found];
};
